import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Menu,
    X,
    ShoppingCart,
    Store
} from "lucide-react";
import ResellerRequestModal from "../common/ResellerRequestModal";
import CartSheet from "../store/CartSheet";
import { usePlatformContext } from "../../context/PlatformContext";
import { useCart } from "../../context/CartContext";
import { useCustomer } from "../../context/CustomerContext";
import { getImageUrl } from "../../utils/imageUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicAxios = axios.create({ withCredentials: false });

export default function Header({ transparent = false }) {
    const { cart, cartCount, setCartOpen } = useCart();
    const { customer } = useCustomer();
    const { platformInfo } = usePlatformContext();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [headerPages, setHeaderPages] = useState([]);
    // Removed local form handling, now using ResellerRequestModal

    useEffect(() => {
        fetchHeaderPages();
    }, []);

    const fetchHeaderPages = async () => {
        try {
            const response = await publicAxios.get(`${API}/pages/public`);
            setHeaderPages(response.data.filter(p => p.show_in_navigation));
        } catch (error) {
            console.error("Failed to fetch header pages:", error);
        }
    };

    const isHome = location.pathname === "/";
    const isResellerProgram = location.pathname === "/reseller-program";

    const handleScrollTo = (id) => {
        if (isHome || isResellerProgram) {
            const element = document.getElementById(id);
            if (element) element.scrollIntoView({ behavior: 'smooth' });
        } else {
            navigate(`/${id === 'shop' ? 'products' : '#' + id}`); // Redirect shop to /products, others to hash?
            // Actually, Features and How it Works are on LandingPage.
            // If we are on ProductDetails, we probably shouldn't show "Features" or "How it Works" links 
            // OR we should redirect to /reseller-program#features
            if (id === 'shop') navigate('/products');
            else navigate(`/reseller-program#${id}`);
        }
        setMobileMenuOpen(false);
    };

    return (
        <>
            <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${transparent ? 'bg-transparent text-white' : 'glass-header bg-white/80 backdrop-blur-lg border-b border-amber-100'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
                            {platformInfo.platform_logo_url ? (
                                <img
                                    src={getImageUrl(platformInfo.platform_logo_url)}
                                    alt={platformInfo.platform_name}
                                    className="h-12 md:h-16 w-auto object-contain"
                                />
                            ) : (
                                <div className="w-10 h-10 bg-gradient-to-br from-amber-600 to-amber-800 rounded-xl flex items-center justify-center shadow-lg">
                                    <Store className="w-5 h-5 text-white" />
                                </div>
                            )}
                            <div className="flex flex-col">
                                <span className={`text-xl font-bold ${transparent ? 'text-white' : 'text-slate-900'}`}>
                                    {platformInfo.platform_name}
                                </span>
                                <span className={`text-[10px] ${transparent ? 'text-slate-200' : 'text-slate-500'} -mt-1`}>
                                    {platformInfo.platform_tagline}
                                </span>
                            </div>
                        </div>



                        <div className="hidden md:flex items-center gap-4">


                            {headerPages.map(page => (
                                <a
                                    key={page._id}
                                    href={`/${page.slug}.html`}
                                    className={`${transparent ? 'text-slate-200 hover:text-white' : 'text-slate-600 hover:text-amber-700'} transition-colors font-medium text-sm`}
                                >
                                    {page.title}
                                </a>
                            ))}


                            <Button
                                className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 rounded-full px-6 shadow-lg shadow-amber-200"
                                onClick={() => navigate('/login')}
                            >
                                Login
                            </Button>
                        </div>

                        <button
                            className="md:hidden p-2"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className={`w-6 h-6 ${transparent ? 'text-white' : 'text-slate-900'}`} />}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden bg-white border-t border-amber-100 p-4 space-y-4">



                        {headerPages.map(page => (
                            <a
                                key={page._id}
                                href={`/${page.slug}.html`}
                                className="block w-full text-left text-slate-600 hover:text-amber-700 font-medium"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {page.title}
                            </a>
                        ))}
                        <button onClick={() => handleScrollTo('how-it-works')} className="block w-full text-left text-slate-600 hover:text-amber-700 font-medium">How it Works</button>
                        <Button
                            className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 rounded-full px-6 shadow-lg shadow-amber-200"
                            onClick={() => navigate('/login')}
                        >
                            Login
                        </Button>


                    </div>
                )}
            </nav >
            <CartSheet onCheckout={() => {
                // ... same as before
            }} />
            <ResellerRequestModal open={isDialogOpen} onOpenChange={setIsDialogOpen} />
        </>
    );
}
